import { Button, Empty, Flex, Space, Table, Typography } from 'antd'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import { useContext, useMemo, useState } from 'react'
import { MemoContext } from './context/context'
import { useParams } from 'react-router-dom'
import { NavContext } from '../../../context/global_context'
import { deleteRequest } from '../../../services/base'
import { SELECT_DTC_MEMO } from './context/context'
import ExpandedRowRender from './ExpandedRowRenderer'
import { FormattedMessage, useIntl } from 'react-intl'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {DeleteOutlined, EditOutlined} from '@ant-design/icons'

const MemoTable = ({
  dataSource,
  showAction,
  // Tracer Memo list page params
  pagination = false,
  getDtcMemos = null,
  onDelete = null,
  // Crowdsource Tracer Memo Popup params
  selectedItem = null,
  setSelectedItem = null,
  memoAction = null,
  totalMemos=null,
  loadMoreMemos = null,
}) => {
  let { vehicle } = useParams()
  const intl = useIntl()
  const [state, dispatch] = useContext(MemoContext)
  const [navState, navDispatch] = useContext(NavContext)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'memos.ecuAcronym' }),
        dataIndex: 'acronym',
        width: 150,
        sorter: (a, b) => a.acronym.localeCompare(b.acronym),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">{text}</EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'memos.dtcName' }),
        dataIndex: 'dtc_name',
        width: 180,
        sorter: (a, b) => a.dtc_name.localeCompare(b.dtc_name),
        render: (text) => {
          return <EllipsisTooltip className="ellipsis">{text}</EllipsisTooltip>
        },
      },
      {
        title: intl.formatMessage({ id: 'memos.description' }),
        dataIndex: 'short_desc',
        render: (text) => (
          <EllipsisTooltip className="ellipsis">{text}</EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'memos.action' }),
        align: 'right',
        ...(showAction ? {width: 75} : {}),
        render: (_, record) => {
          const isExpanded = expandedRowKeys.includes(record.rowKey)
          const isSelectedItem = record.id === selectedItem?.id

          return showAction ? (
            <Button
              type="link"
              className={`${isExpanded || record.status === 'rejected' ? 'link-button neutral' : 'link-button'}`}
              onClick={() => {
                toggleExpand(record.rowKey)
                setSelectedItem(!isExpanded ? record : null)
              }}
            >
              {isSelectedItem ? (
                <>{intl.formatMessage({ id: 'forms.close' })}</>
              ) : (
                <>{intl.formatMessage({ id: 'forms.review' })}</>
              )}
            </Button>
          ) : (
            <TableActions
              actions={[
                {
                  key: 'edit',
                  label: intl.formatMessage({ id: 'forms.edit' }),
                  icon: <EditOutlined />,
                  onClick: () => {
                    dispatch({
                      type: SELECT_DTC_MEMO,
                      data: { selected_memo: record, show_memo_popup: true },
                    })
                  },
                },
                {
                  key: 'delete',
                  label: intl.formatMessage({ id: 'forms.delete' }),
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    onDelete(record.id)
                  },
                },
              ]}
            />
          )
        },
      },
    ],
    [dataSource, pagination, showAction, selectedItem, expandedRowKeys]
  )

  const toggleExpand = (id) => {
    setExpandedRowKeys((prev) => {
      const keys = prev.includes(id) ? prev.filter((key) => key !== id) : [id]
      return keys
    })
  }

  return (
    <Space
      direction="vertical"
      size={'middle'}
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey="rowKey"
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        {...showAction ? {
          scroll: {
            y: '70vh'
          }
        } : {}}
        expandable={{
          expandIcon: () => null,
          showExpandColumn: false,
          expandedRowKeys,
          rowExpandable: () => true,
          expandedRowRender: (record, index, indent) => (
            <ExpandedRowRender
              record={record}
              index={index}
              memoAction={memoAction}
            />
          ),
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total, range) => <RecordsCount count={total} />,
                onChange: (page) => {
                  // 10 is page size
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getDtcMemos(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty
                description={ <RecordsCount count={0} /> }
              />
            </div>
          ),
        }}
      />
      {!loadMoreMemos &&
        pagination &&
        pagination?.totalPages <= 1 &&
        pagination?.totalCount > 0 && (
          <Typography.Text>
            <RecordsCount count={pagination?.totalCount} />
          </Typography.Text>
        )}
      {loadMoreMemos && (
        <Flex justify="space-between">
          <Typography.Text>
            <RecordsCount count={dataSource.length} total={totalMemos} msgId={'generic.showingRecords'}/>
          </Typography.Text>

          <Button
            type="link"
            className="link-button"
            onClick={() => {
              loadMoreMemos()
            }}
          >
            {intl.formatMessage({ id: 'memos.loadMore' })}
          </Button>
        </Flex>
      )}
      {!loadMoreMemos &&
       !pagination && (
          <Typography.Text>
            <RecordsCount count={dataSource.length} total={totalMemos} msgId={'generic.showingRecords'}/>
          </Typography.Text>
      )}
    </Space>
  )
}

export default MemoTable
